<template>
  <div class="menu-sidebar-item">
    <div
      @click="handleLinkClick"
      :class="[
        'menu-sidebar-item__category-container',
        { 'menu-sidebar-item__category-container--active': shouldHighlightContainer },
      ]"
    >
      <div class="menu-sidebar-item__icon">
        <img v-if="menuIcon" :src="menuIcon" :alt="item.name" />
      </div>

      <transition name="fade-slide-right">
        <span v-if="shouldShowCategory" class="menu-sidebar-item__category">
          <div v-if="item.blank">
            <a :href="item.url" target="_blank">
              {{ $t(item.name) }}
            </a>
          </div>

          <template v-else>
            <component :is="linkComponent" v-bind="linkProps">
              {{ $t(item.name) }}
              <img
                v-if="item.id === 'sms'"
                class="menu-sidebar-item__new"
                :src="require(`@/assets/svgs/star.svg`)"
                :alt="item.name"
              />

              <span v-if="shouldShowArrow" class="menu-sidebar-item__arrow-icon">
                <i class="menu-sidebar-item__arrow"></i>
              </span>
            </component>
          </template>
        </span>
      </transition>
    </div>

    <transition name="slide-fade">
      <div v-if="shouldShowSubcategories" class="menu-sidebar-item__subcategory">
        <router-link
          v-for="subItem in subLevelItems"
          :key="subItem.id"
          :to="{ name: subItem.id }"
          exact-path
          :class="getSubItemClasses(subItem)"
          @click="trackSubcategoryClick(subItem)"
        >
          {{ $t(subItem.name) }}
        </router-link>
      </div>
    </transition>
  </div>
</template>

<script>
import bus from '@/helpers/events/bus';
import { useMixpanelConfig } from '@/utilities/mixpanel';
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'MenuSidebarItem',
  props: {
    isSidebarExpanded: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      default: () => {},
    },
    color: {
      type: String,
      default: '#888',
    },
    subLevels: {
      type: Array,
      default: null,
    },
    idActive: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      categorieContainer: {
        isVisible: false,
      },
    };
  },
  computed: {
    menuIcon() {
      const iconMap = {
        'oto-academy': 'oto-academy.svg',
        'base-management': 'base-management.svg',
        campaigns: 'campaigns.svg',
        email: 'email.svg',
        sms: 'sms.svg',
        store: 'store-team-management.svg',
        integrations: 'data-and-integrations.svg',
        cashback: 'cashback.svg',
        analytics: 'analytics-o2o.svg',
        settings: 'settings.svg',
        help: 'help.svg',
      };

      const iconName = iconMap[this.item.id];
      return iconName ? require(`@/assets/svgs/menu/${iconName}`) : null;
    },

    shouldHighlightContainer() {
      return this.isItemActive && !this.isSidebarExpanded;
    },

    checkActiveItem() {
      return this.idActive === this.item.id;
    },

    hasSubcategoriesForItem() {
      if (this.item.id === 'settings') return;
      return this.hasSubcategories(this.item.id);
    },

    subLevelItems() {
      return this.buildSubLevel(this.item.id) || [];
    },

    linkComponent() {
      return this.hasSubcategoriesForItem ? 'a' : 'router-link';
    },

    linkProps() {
      if (this.hasSubcategoriesForItem) {
        return {};
      }
      return { to: this.item.url };
    },

    shouldShowArrow() {
      if (this.item.id === 'settings') return;
      return this.hasSubcategoriesForItem;
    },

    hasActiveSubRoute() {
      if (!this.hasSubcategoriesForItem) return false;
      return this.subLevelItems.some(subItem => this.$route.name === subItem.id);
    },

    isItemActive() {
      if (this.$route.name === this.item.id) return true;
      return this.hasActiveSubRoute;
    },

    shouldShowCategory() {
      return this.isSidebarExpanded && !!this.item.name;
    },

    shouldShowSubcategories() {
      return this.hasSubcategoriesForItem && this.categorieContainer.isVisible && this.isSidebarExpanded;
    },

    getSubItemClasses() {
      return subItem => {
        return [
          'menu-sidebar-item__sublevel',
          { 'menu-sidebar-item__sublevel--active': this.$route.name === subItem.id },
        ];
      };
    },
  },
  mounted() {
    bus.$on('sidebar-visible-sublevels', () => {
      this.categorieContainer.isVisible = !!this.checkActiveItem;
    });

    this.openCategoryIfHasActiveRoute();
  },
  watch: {
    '$route.name': 'openCategoryIfHasActiveRoute',

    isSidebarExpanded(isExpanded) {
      if (isExpanded) {
        this.openCategoryIfHasActiveRoute();
      }
    },
  },
  beforeUnmount() {
    bus.$off('sidebar-visible-sublevels');
  },
  methods: {
    /**
     * @description check if it has subcategories
     */
    hasSubcategories(id) {
      return this.subLevels.find(el => el.id.includes(id));
    },

    /**
     * @description get routes to build sublevels
     */
    buildSubLevel(id) {
      return this.subLevels.find(el => el.id === id)?.routes;
    },

    openCategoryIfHasActiveRoute() {
      if (this.hasActiveSubRoute) {
        this.categorieContainer.isVisible = true;
      }
    },

    /**
     * @description toggle subcategories container
     */
    toggle() {
      this.categorieContainer.isVisible = !this.categorieContainer.isVisible;
    },

    trackSidebarNavigation(itemId) {
      useMixpanelConfig('sidebar_clicked', {
        selectedItem: itemId,
      });
    },

    /**
     * @description handle click on menu item - only toggle if has subcategories
     */
    handleLinkClick(event) {
      if (!this.hasSubcategoriesForItem) {
        this.trackSidebarNavigation(this.item.id);
        return;
      }

      event.preventDefault();
      this.toggle();
    },

    trackSubcategoryClick(subItem) {
      this.trackSidebarNavigation(subItem.id);
    },
  },
});
</script>

<style lang="scss" scoped>
.menu-sidebar-item {
  color: $gray-950;
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
  margin-bottom: 4px;
  transition: all 0.2s ease;
  cursor: pointer;

  &__new {
    margin-left: 6px;
  }

  &__icon {
    width: 24px;
    height: 24px;
  }

  &__category-container {
    border-radius: 8px;
    padding: 8px 12px;
    display: flex;
    align-items: center;
    gap: 12px;
    transition: background-color 0.3s ease;

    &:hover {
      background-color: $pink-100;
    }

    &--active {
      background-color: $pink-100;
      font-weight: 600;
    }
  }

  &__category {
    flex: 1;
    display: flex;
    align-items: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    a,
    .router-link-active,
    .router-link-exact-active {
      display: flex;
      align-items: center;
      width: 100%;
      color: inherit;
      text-decoration: none;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  &__arrow-icon {
    width: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: auto;
    transition: transform 0.3s ease;
  }

  &__arrow {
    display: inline-block;
    border: solid $gray-500;
    border-width: 0 1px 1px 0;
    border-radius: 0;
    padding: 3px;
    transform: rotate(45deg);
    transition: transform 0.3s ease;
  }

  &:has(.menu-sidebar-item__subcategory) {
    .menu-sidebar-item__arrow {
      transform: rotate(-135deg);
    }
  }

  &__subcategory {
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-top: 4px;
  }

  &__sublevel {
    display: block;
    font-weight: 400;
    color: $gray-800;
    border-radius: 8px;
    padding: 8px 12px 8px 48px;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    margin-bottom: 4px;
    cursor: pointer;
    text-decoration: none;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &:hover {
      background-color: $pink-100;
      transform: translateX(2px);
    }

    &--active {
      background: $pink-100;
      border-radius: 8px;
      font-weight: 600;
    }
  }
}

.tag-new {
  border-radius: 100px;
  font-family: 'Montserrat', sans-serif;
  font-size: 8px;
  font-weight: 700;
  line-height: 17px;
  color: #ffffff;
  background: #ff3162;
  position: absolute;
  right: 0;
}

.slide-fade-enter-active {
  transition: all 0.5s cubic-bezier(0.05, 0.9, 0.2, 1);
  transition-delay: 0.15s;
}

.slide-fade-leave-active {
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  transition-delay: 0.1s;
}

.slide-fade-enter-from {
  transform: translateY(-8px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateY(-6px);
  opacity: 0;
}

.fade-slide-right-enter-active {
  transition: all 0.2s ease-out;
  transition-delay: 0.2s;
}

.fade-slide-right-leave-active {
  transition: all 0.2s ease-in;
}

.fade-slide-right-enter-from {
  transform: translateX(-15px);
  opacity: 0;
}

.fade-slide-right-leave-to {
  transform: translateX(-10px);
  opacity: 0;
}
</style>
